import { RENDER_ADAPTER_REGISTRY } from '../render-adapters/adapters';
import type { RenderAdapterComponentType } from '../render-adapters/types';
import type { FormStateValue } from '@de/providers/DataSourceManager.types';
import { formStateValueToKeys } from '@de/utils/form-state';
import { dropRestrictedContextInputs } from '@de/components/HostSlot/inputPrivacy';
import { HOST_SLOT_OUTPUT_POLICY } from '@de/utils/action-resolver/hostSlotOutputActions';
import type { ComponentBindingMeta } from './types';

/**
 * Central default {@link ComponentBindingMeta} registry, keyed by
 * hen-ui component type name (ADR Seam 1 / F1). Today it carries only
 * the self-managed `controlledState` descriptors for strictly-controlled
 * components that must work in a DSL without a bespoke wrapper.
 *
 * {@link getBindingMeta} is the single seam through which the binding
 * enhancer resolves these defaults. Keeping the lookup behind one
 * accessor is the ADR guardrail (#2) that lets the descriptor source
 * later move to a generated catalog artifact without touching any call
 * site.
 *
 * Consumer-supplied `metaByComponent` entries still win over this
 * registry (see `applyBindings`), so explicit per-render overrides are
 * unaffected.
 */
/**
 * Shared expand / modal controlled-state tuples for the ranking-list
 * cards. `RankingCard` and `SponsoredCard` expose the identical hen-ui
 * contract (`expanded`/`onExpandedChange`, `modalOpen`/`onModalOpenChange`,
 * both defaulting to `false`) and are interleaved into the same rankings
 * list, so they share one descriptor to stay in lock-step.
 *
 * `compared` / `onComparedChange` is intentionally excluded — that is
 * cross-row provider state (Seam 2 / ComparisonProvider), not local
 * ephemeral state, and is gated behind `showCompare` (default false).
 * Backing it with a local `useState` would be semantically wrong.
 */
const EXPAND_MODAL_CONTROLLED_STATE: ComponentBindingMeta['controlledState'] = [
  { valueProp: 'expanded', changeProp: 'onExpandedChange', default: false },
  { valueProp: 'modalOpen', changeProp: 'onModalOpenChange', default: false },
];

/**
 * Props on the ranking-list cards that carry untrusted CMS rich HTML
 * (the `children` blurb). Listed here so the binding enhancer routes a
 * bound string value through the sanitized RichText / parseHtmlString
 * sink. The `variant` renders the blurb at the card's `text-b5` slot
 * typography (surfaced here rather than hardcoded in the enhancer).
 * When the `getBindingMeta` accessor seam later resolves its source
 * from the generated catalog artifact, this designation moves there
 * and becomes config-authorable with no call-site change.
 */
const RANKING_CARD_HTML_PROPS: ComponentBindingMeta['htmlProps'] = [
  { prop: 'children', variant: 'b5' },
];

/**
 * Every surface in `RENDER_ADAPTER_REGISTRY`, registered so `_bind.rows` on a
 * node of that type projects the source into its whole prop object.
 *
 * Derived from the registry rather than listed, so the two cannot drift: an
 * adapter added there is bindable here with no second edit, and an adapter
 * deleted there stops being registered rather than leaving a dangling entry.
 *
 * The registry is keyed by Puck component type, which for a hen-ui component
 * is its barrel export name — the same key `getBindingMeta` takes — so no
 * mapping table sits between them.
 */
const STATUS_FRAME_TYPES: ReadonlySet<string> = new Set([
  'BarChart',
  'LineChart',
  'DataTable',
  'HeatmapChart',
  'WaterfallChart',
  'ComboChart',
  'KPIBanner',
]);

const ADAPTER_BINDING_META: Readonly<Record<string, ComponentBindingMeta>> =
  Object.fromEntries(
    (
      Object.entries(RENDER_ADAPTER_REGISTRY) as ReadonlyArray<
        [RenderAdapterComponentType, ComponentBindingMeta['adapter']]
      >
    ).map(([type, adapter]) => [
      type,
      STATUS_FRAME_TYPES.has(type)
        ? { adapter, statusFrame: true }
        : { adapter },
    ]),
  );

/**
 * The multi-value form-state convention, shared by every control that can hold
 * more than one selection.
 *
 * The write side stores the selection as it came: form state holds a list, so
 * there is nothing to encode. The read side goes through
 * {@link formStateValueToKeys}, which also accepts the comma-joined string a
 * multi-value control wrote before form state could hold a list — seeded initial
 * state and query-param hydration still produce that shape, and a control whose
 * displayed selection silently emptied on hydration is worse than splitting a
 * value that was already encoded that way.
 *
 * Deliberately not comma-joining on write. That encoding cannot round-trip a
 * value containing a comma, and for a dimension-value filter
 * (`"Washington, D.C."`) the result is a wrong query rather than a cosmetic
 * problem.
 */
/**
 * Shared by every control below: a Radix-backed input renders one element and
 * spreads what it does not recognise onto it. None of them can draw a skeleton
 * or an empty state, so the source lifecycle has nowhere to go except the DOM.
 */
const NO_LIFECYCLE_PROPS = { lifecycleProps: 'omit' } as const;

const MULTI_VALUE_FORM_STATE = {
  formKeyValueTransform: (value: unknown): FormStateValue =>
    Array.isArray(value)
      ? value.filter(
          (entry): entry is string =>
            typeof entry === 'string' && entry.length > 0,
        )
      : typeof value === 'string'
        ? value
        : '',
  formStateValueTransform: (raw: FormStateValue | undefined): unknown =>
    formStateValueToKeys(raw),
} as const;

/**
 * The hen-ui select family.
 *
 * `Select` is single-value and Radix-backed: it emits `onValueChange` with a
 * string and reads `value`. `MultiSelect` is a different contract for the same
 * job — `onChange` with a `string[]`, read back through `selectedValues` — so it
 * carries the multi-value convention above rather than a second one of its own.
 */
const SELECT_FAMILY_BINDING_META: Readonly<
  Record<string, ComponentBindingMeta>
> = {
  Select: {
    ...NO_LIFECYCLE_PROPS,
    eventPropNames: ['onValueChange', 'onChange', 'onSelect'],
    valuePropName: 'value',
    // Cascading-select convenience: when a Select's resolved options narrow to
    // a single choice (e.g. picking `degree: general` collapses
    // category/subject to a lone `general`), commit it automatically instead of
    // forcing the user to open a one-item dropdown.
    autoSelectSingleOption: true,
    // A Select that renders with a preselected option records that value as
    // the answer. The enhancer only does this when the meta opts in.
    seedDefaultValue: true,
  },
  MultiSelect: {
    ...NO_LIFECYCLE_PROPS,
    eventPropNames: ['onChange'],
    valuePropName: 'selectedValues',
    ...MULTI_VALUE_FORM_STATE,
  },
};

/**
 * Binding meta for hen-ui components whose event / value contract the binding
 * enhancer cannot guess.
 *
 * Every entry here describes a hen-ui prop contract, not a surface: `Select`
 * emits on `onValueChange` (Radix listbox) rather than the enhancer's default
 * `onChange` / `onSelect` wherever it is rendered. These lived in the DSL
 * surface's own override table, which meant a second host wiring `applyBindings`
 * either forked the table or silently got no form wiring at all. They belong
 * to the component, so they live with the component keys.
 *
 * A host can still override any entry: `applyBindings` prefers its
 * `metaByComponent` argument and only falls back here.
 *
 * Components that manage their own internal state — presentational primitives —
 * should have no entry. Components that carry a controlled value need
 * `valuePropName` so the enhancer keeps them controlled from
 * `formState[formKey]` without the author spelling out `_bind.value`.
 */
/**
 * hen-ui gives these an `onClick` button semantics (role, tab stop, focus
 * ring; for Link / Tag Link, only without a web address), so a click that
 * only carries tracking is heard on the `Trackable` wrapper as it bubbles,
 * and their DOM stays as it is.
 */
const TRACKING_ONLY_WRAPPER_CLICK: ComponentBindingMeta = {
  trackingOnlyClickOnWrapper: true,
};

const HEN_UI_BINDING_META: Readonly<Record<string, ComponentBindingMeta>> = {
  ...SELECT_FAMILY_BINDING_META,
  // `Card` hosts arbitrary authored children (`Card.Content` slots routinely
  // carry their own tracked Buttons/Links) under the same root the wrapper
  // click marker would stamp. `suppressClickOwnershipMarker` keeps a
  // configured click on the card's own wrapper click from also silencing a
  // nested child's independent host `ElementClicked`.
  Card: { ...TRACKING_ONLY_WRAPPER_CLICK, suppressClickOwnershipMarker: true },
  Icon: TRACKING_ONLY_WRAPPER_CLICK,
  Image: TRACKING_ONLY_WRAPPER_CLICK,
  Link: TRACKING_ONLY_WRAPPER_CLICK,
  TagLink: TRACKING_ONLY_WRAPPER_CLICK,
  // Register the Button click slot with the binding enhancer so
  // `composeEventHandlers` recognises `onClick` as a wireable event
  // prop. Without an entry here, a
  // tracking-only Button (`_meta.tracking: { kind: 'product' }` with no
  // `_action.onClick`) gets queued under the default `eventPropNames`
  // (`['onChange', 'onSelect']`) — neither of which exists on Button —
  // and the click-dispatch slot is never wrapped. With this entry the
  // click dispatcher attaches to `onClick` and the Button fires
  // `ProductClicked` even when the DSL config declares no authored
  // handler. `valuePropName` is intentionally omitted: Buttons don't
  // carry a controlled value.
  Button: {
    ...NO_LIFECYCLE_PROPS,
    eventPropNames: ['onClick'],
  },
  // Same click wiring as Button, which PillButton renders.
  PillButton: {
    ...NO_LIFECYCLE_PROPS,
    eventPropNames: ['onClick'],
  },
  // Free-text input. HEN-UI `Input` fires the native `onChange(event)` and
  // reads its value from the `value` prop. Declaring `valuePropName: 'value'`
  // keeps it controlled from `formState[formKey]` so the field stays
  // authoritative across the DSM-driven re-renders (and respects
  // `formState.clear`) instead of relying on uncontrolled DOM state.
  // A prefilled value is seeded as the answer; a phone lead field is formatted.
  Input: {
    ...NO_LIFECYCLE_PROPS,
    eventPropNames: ['onChange'],
    valuePropName: 'value',
    seedDefaultValue: true,
    omitDefaultValueWhenBound: true,
    seedAwaitsTemplatePaths: true,
    formatLeadField: true,
  },
  // Multi-line free text, same contract as `Input` minus lead-field formatting.
  Textarea: {
    ...NO_LIFECYCLE_PROPS,
    eventPropNames: ['onChange'],
    valuePropName: 'value',
    seedDefaultValue: true,
    omitDefaultValueWhenBound: true,
    seedAwaitsTemplatePaths: true,
  },
  // Radix radio group. Selection is reported on `onValueChange` as a string
  // and read back from `value`, so formState[formKey] stays the controlled
  // choice and a clear deselects it.
  Radio: {
    ...NO_LIFECYCLE_PROPS,
    eventPropNames: ['onValueChange'],
    valuePropName: 'value',
  },
  // Radix ToggleGroup pills. `onValueChange` yields a `string` for
  // single-select (`type="single"`, e.g. a degree step rendered with
  // `platform.dataRepeater` toggle items) and a `string[]` for multi-select
  // filter pills (`type="multiple"`). One entry serves both: the write side
  // normalizes either onto the shared multi-value convention, and the read side
  // always rehydrates to an array — hen-ui's ToggleGroup normalizes that back to
  // a string for single (first entry) or keeps the array for multiple.
  // `valuePropName: 'value'` keeps the group controlled (selected pills stay
  // highlighted) when no explicit `_bind.value` is authored.
  ToggleGroup: {
    ...NO_LIFECYCLE_PROPS,
    eventPropNames: ['onValueChange'],
    valuePropName: 'value',
    ...MULTI_VALUE_FORM_STATE,
  },
  // CTA lives on the nested anchor (`onCtaClick` from hen-ui), not the
  // root div's `onClick`. Without `clickPropName`, product click dispatch
  // would queue the default `onClick` slot and never fire
  // `ProductClicked`.
  StudentLoansBanner: {
    eventPropNames: ['onCtaClick'],
    clickPropName: 'onCtaClick',
    outboundUrlPropPath: 'cta.href',
  },
  // Mirrors `StudentLoansBanner` — CTA click lives on `onCtaClick`, not
  // the root Card's `onClick`. Used on S3 (results row injection) and S5
  // (ExitStrategy drawer). `outboundUrlPropPath` extracts the destination
  // URL from `cta.href` for the `ProductClicked` wire payload.
  StudentLoansInline: {
    eventPropNames: ['onCtaClick'],
    clickPropName: 'onCtaClick',
    outboundUrlPropPath: 'cta.href',
  },
  // CTA lives on the nested Visit Site control (`onVisitSiteClick` from
  // hen-ui), not the root's `onClick`. Without `clickPropName`, product click
  // dispatch would queue the default `onClick` slot — Read More
  // expand/collapse would bubble and fire `ProductClicked` (EDUN-14734).
  // Lets a DSL author dispatch an action (e.g. `navigation.linkout`) from
  // the card CTA instead of binding a static `visitSiteHref`. No
  // `valuePropName` — the card carries no controlled value.
  //
  // The Read More toggle and the compare checkbox are separate, independent
  // interactive descendants under the same root the enhancer's catch-all
  // props spread reaches. `suppressClickOwnershipMarker` keeps a configured
  // binding on `onVisitSiteClick` from also marking (and so silencing) the
  // Read More toggle's own host `ElementClicked`.
  EditorialListing: {
    eventPropNames: ['onVisitSiteClick'],
    clickPropName: 'onVisitSiteClick',
    suppressClickOwnershipMarker: true,
  },
  // Popover renders its own trigger and reports its clicks on
  // `onTriggerClick`; the panel toggles after the handler runs. Popover
  // forwards `data-*` props to the trigger, so the click-ownership marker
  // lands on the trigger and not on the panel content.
  Popover: {
    ...NO_LIFECYCLE_PROPS,
    eventPropNames: ['onTriggerClick'],
    clickPropName: 'onTriggerClick',
  },
};

export const DEFAULT_COMPONENT_BINDING_META: Readonly<
  Record<string, ComponentBindingMeta>
> = {
  ...ADAPTER_BINDING_META,
  ...HEN_UI_BINDING_META,
  RankingCard: {
    controlledState: EXPAND_MODAL_CONTROLLED_STATE,
    htmlProps: RANKING_CARD_HTML_PROPS,
  },
  SponsoredCard: {
    controlledState: EXPAND_MODAL_CONTROLLED_STATE,
    htmlProps: RANKING_CARD_HTML_PROPS,
  },
  'platform.hostSlot': {
    forwardSourcesPending: true,
    filterAuthoredProps: dropRestrictedContextInputs,
    collectActionsAs: 'outputs',
    outputPolicy: HOST_SLOT_OUTPUT_POLICY,
  },
};

/**
 * Resolve the default {@link ComponentBindingMeta} for a hen-ui
 * component type, or `undefined` when the registry has no entry.
 *
 * This is the single lookup seam for the controlled-state descriptor
 * source (ADR guardrail #2): the underlying data can later be swapped
 * for a generated catalog artifact without changing callers.
 */
export function getBindingMeta(type: string): ComponentBindingMeta | undefined {
  return DEFAULT_COMPONENT_BINDING_META[type];
}
